import { ShieldCheck, Users, Building, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EnterpriseReadinessProps {
  data: {
    title: string;
    subtitle: string;
    features: Array<{
      title: string;
      description: string;
    }>;
  };
  className?: string;
}

const iconMap = {
  'SOC2 readiness (Q4)': ShieldCheck,
  'SSO/SAML': Users,
  'Dedicated tenancy': Building,
  'U.S. based onboarding': MapPin,
};

/**
 * Enterprise Readiness Section Component
 * Shows enterprise-grade features and compliance
 */
export function EnterpriseReadiness({ data, className }: EnterpriseReadinessProps) {
  return (
    <section id="enterprise" className={cn("py-20 bg-background", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold">
            {data.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {data.subtitle}
          </p>
        </div>

        {/* Enterprise Features */}
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {data.features.map((feature, index) => {
              const IconComponent = iconMap[feature.title as keyof typeof iconMap] || ShieldCheck;
              
              return (
                <div key={index} className="gradient-card rounded-2xl p-8 shadow-card hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Enterprise Info */}
          <div className="mt-12 text-center">
            <div className="gradient-card rounded-2xl p-8 shadow-card">
              <h3 className="text-2xl font-bold mb-4">Ready for Preview</h3>
              <p className="text-lg text-muted-foreground mb-6">
                NeurosEdge is already running in preview across Polaris infrastructure. 
                Now available for partners, customers, analysts, and media.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  ✓ Preview Ready
                </span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                  ✓ Enterprise Scale
                </span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  ✓ Security First
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
